import { EntityByEntityTypeId, GenericEntityConstantsEntities } from "../../../api/GenericConstants";
import { LucideIcon } from "../../icon/LucideIcon";
import { EntityConstants, ISoftDeletable } from "../../../api/GenericTypes";
import { IGenericEntityPermissions } from "../../permissions/useEntityPermissions";
import { Routes } from "../../routes/useEntityRoutes";
import { LinkButton } from "../../buttons/LinkButton/LinkButton";

/**
 * Props for the EntityEditButton component.
 * @author @CorradoSurmanowicz
 * @template EntityTypeId - The type of the entity's identifier, extending `GenericEntityConstantsEntities`.
 * @property entity - The entity instance to be edited.
 * @property entityConstants - Constants describing the entity type (e.g., singular name).
 * @property permissions - Permission utilities for the entity, including `canEdit`.
 * @property routes - Route utilities for the entity, including `getEditLink`.
 * @property disabledOverride - Optional flag to force the button disabled state.
 */
interface EntityEditButtonProps<EntityTypeId extends GenericEntityConstantsEntities> {
  entity: EntityByEntityTypeId<EntityTypeId>;
  entityConstants: EntityConstants;
  permissions: IGenericEntityPermissions<EntityTypeId>;
  routes: Routes;
  disabledOverride?: boolean;
  labelOverride?: string;
}

/**
 * Renders an edit button for a given entity, handling permissions and soft-delete state.
 * @author @CorradoSurmanowicz
 * @template EntityTypeId - The type of the entity's identifier, extending `GenericEntityConstantsEntities`.
 * @param {EntityEditButtonProps<EntityTypeId>} props - The props for the EntityEditButton component.
 * @returns {JSX.Element} A `LinkButton` component that navigates to the entity's edit page, disabled if the user lacks edit permissions or the entity is soft-deleted.
 */
export const EntityEditButton = <EntityTypeId extends GenericEntityConstantsEntities>({
  entity,
  entityConstants,
  permissions,
  routes,
  disabledOverride,
  labelOverride,
}: EntityEditButtonProps<EntityTypeId>) => {
  if (Object.hasOwn(entity, "isDeleted") && (entity as ISoftDeletable).isDeleted) return <></>;

  return (
    <LinkButton
      className="btn btn-default"
      title={`Edit ${entityConstants.entitySingular}`}
      linkTo={routes.getEditLink(+entity.id)}
      disabled={
        disabledOverride
          ? disabledOverride
          : Object.hasOwn(entity, "isDeleted")
          ? !permissions.canEdit(entity) || !!(entity as ISoftDeletable).isDeleted
          : !permissions.canEdit(entity)
      }
    >
      <LucideIcon name="square-pen" /> {labelOverride || "Edit"}
    </LinkButton>
  );
};
